import { ComponentIds, Entity, OutlineSystem } from '@inter-ikea-core/core';
import { reportRedo, reportUndo } from 'analytics/reporters';
import { useCore } from 'contexts/core';
import { useSceneHandler } from 'contexts/sceneHandler';
import { getConflicts, resetConflicts } from 'ducks/core';
import { useAppDispatch } from 'ducks/hooks';
import { isDoorsVisible, isRoomConfigurationActive } from 'ducks/layout';
import { getFutureRoomSizeHistory, getPastRoomSizeHistory } from 'ducks/roomSizeHistory';
import React, { useCallback, useEffect, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { animated, useSpring } from 'react-spring';
import { AppRoutes } from 'routes';
import { RoomChangeComponent, ShoppingBagComponent } from '@inter-ikea-storageone/range';
import styled from 'styled-components';
import HistoryFabs from './HistoryFabs';

const BtnGroup = styled.div`
  display: flex;

  & > * + * {
    margin-left: 1rem;
  }
`;

type HistoryState = {
  canUndo: boolean;
  canRedo: boolean;
};

type Props = React.PropsWithoutRef<JSX.IntrinsicElements['div']>;

export const useHistoryEventTrigger = () => {
  // Triggers rerender of component after a history event
  const coreApi = useCore();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setEntitiesInScene] = useState<Entity[]>([]);

  const onHistoryEvent = useCallback(() => {
    setEntitiesInScene(coreApi.entities.getByComponent(ShoppingBagComponent.id));
  }, [coreApi.entities]);

  useEffect(() => {
    return coreApi.subscribe('history', onHistoryEvent);
  }, [coreApi, onHistoryEvent]);
};

const History = ({ ...props }: Props) => {
  const doorsVisible = useSelector(isDoorsVisible);
  const conflicts = useSelector(getConflicts);
  const roomConfigurationActive = useSelector(isRoomConfigurationActive);
  const dispatch = useAppDispatch();
  const coreApi = useCore();
  const { setLocalScene } = useSceneHandler();
  const history = useHistory();
  const scene = coreApi.entities.getScene();

  const animationProps = useSpring({
    opacity: !roomConfigurationActive ? 1 : 0,
    display: 'inherit'
  });

  const pastRoomSizeHistory = useSelector(getPastRoomSizeHistory);
  const futureRoomSizeHistory = useSelector(getFutureRoomSizeHistory);

  const getHistoryState = useCallback(
    (): HistoryState => ({
      canUndo: coreApi.history.canUndo(),
      canRedo: coreApi.history.canRedo()
    }),
    [coreApi]
  );

  const [state, setState] = useState<HistoryState>(getHistoryState());

  useEffect(() => {
    const onChange = () => {
      if (history.location.pathname === AppRoutes.PLANNER && coreApi.history.historyEnabled) {
        setState(getHistoryState());
        setLocalScene();
      }
    };

    const onHistoryChange = () => {
      const doors = coreApi.entities.getByComponent(ComponentIds.DOOR_FRONT_COMPONENT_ID);
      if (doorsVisible) {
        coreApi.entities.setAllVisible(doors, true, true);
      } else {
        coreApi.entities.setAllVisible(doors, false, true);
      }
    };

    const unsubscribers = [
      coreApi.subscribe('history', onChange),
      coreApi.subscribe('load', onChange),
      coreApi.subscribe('history', onHistoryChange)
    ];

    coreApi.kvadratRoom?.kvadrat?.onBeforeRoomChange.subscribe(promise => {
      promise.then(() => {
        // Room changed
        scene?.addComponent(new RoomChangeComponent());
      });
    });

    return () => {
      unsubscribers.forEach(callback => {
        callback();
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    coreApi,
    doorsVisible,
    getHistoryState,
    pastRoomSizeHistory,
    futureRoomSizeHistory,
    dispatch,
    history.location.pathname,
    setLocalScene
  ]);

  const undoConflict = () => {
    dispatch(resetConflicts());
    coreApi.entities.unselect();
    reportUndo();
  };

  const undo = () => {
    coreApi.validations['WallVisibilityValidation']?.disable();
    coreApi.validations['SeeThroughFramesValidation']?.disable();
    coreApi.getSystem<OutlineSystem>('OutlineSystem')?.disable();
    coreApi.history.undo();
    coreApi.validations['WallVisibilityValidation']?.enable();
    coreApi.validations['SeeThroughFramesValidation']?.enable();
    coreApi.getSystem<OutlineSystem>('OutlineSystem')?.enable();
    coreApi.emit.onUndo();
    reportUndo();
  };

  const redo = () => {
    coreApi.validations['WallVisibilityValidation']?.disable();
    coreApi.validations['SeeThroughFramesValidation']?.disable();
    coreApi.getSystem<OutlineSystem>('OutlineSystem')?.disable();
    coreApi.history.redo();
    coreApi.validations['WallVisibilityValidation']?.enable();
    coreApi.validations['SeeThroughFramesValidation']?.enable();
    coreApi.getSystem<OutlineSystem>('OutlineSystem')?.enable();
    coreApi.emit.onRedo();
    reportRedo();
  };

  const undoHandler = () =>
    !roomConfigurationActive && (conflicts.length === 0 ? undo() : undoConflict());

  const redoHandler = () => !roomConfigurationActive && redo();

  useHotkeys('ctrl+z', undoHandler, [roomConfigurationActive]);
  useHotkeys('cmd+z', undoHandler, [roomConfigurationActive]);

  useHotkeys('ctrl+shift+z', redoHandler, [roomConfigurationActive]);
  useHotkeys('cmd+shift+z', redoHandler, [roomConfigurationActive]);

  return (
    <animated.div style={animationProps}>
      <BtnGroup {...props} data-testid="history-buttons">
        <HistoryFabs
          undoHandler={undoHandler}
          redoHandler={redoHandler}
          canUndo={state.canUndo}
          canRedo={state.canRedo}
        />
      </BtnGroup>
    </animated.div>
  );
};

export default History;
